import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import { Dispatch, RootState } from '../../../../store/store';
import { changeControllerPowerShare, consoleButtonActions } from '../PowerShare/powerShareActions';
import { CONTROLLER_ACTIONS_BY_ROLE } from '../../../../constants/controller-allowed-actions';
import { CHANNEL_ACTIONS_BY_ROLE } from '../../../../constants/channel-allowed-actions';
import { EXTENDED_INTERVAL_UPDATE_DATA } from '../../../../constants/common';
import { ChangeChannelAction } from '../../../../enums/action';
import { changeChannelActionSilent, CHANNEL_SETTINGS_LIMITS } from '../EditChannel/channelActions';
import { IChannelConsoleSetup } from '../../../../types/channel-types';
import { channelMatchesConsolePattern, CONSOLE_PATTERNS } from './consolePatterns';
import InteractiveButton from '../../../../components/InteractiveButton/InteractiveButton';
import SpinnerLoader from '../../../../components/SpinnerLoader/SpinnerLoader';
import { ButtonType } from '../../../../types/button-type';
import { DEFAULT_INPUT_CLASS } from '../../../../components/InputControl/constants';

type ComponentProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

type ConsoleField = 'shunt_number' | 'machine_number' | 'phase_number';

const FIELD_ACTIONS: Record<ConsoleField, ChangeChannelAction> = {
  shunt_number: ChangeChannelAction.SET_CHANNEL_SHUNT_NUMBER,
  machine_number: ChangeChannelAction.SET_CHANNEL_MACHINE_NUMBER,
  phase_number: ChangeChannelAction.SET_CHANNEL_PHASE_NUMBER,
};

const FIELD_LIMITS: Record<ConsoleField, { MIN: number; MAX: number }> = {
  shunt_number: CHANNEL_SETTINGS_LIMITS.SHUNT_NUMBER,
  machine_number: CHANNEL_SETTINGS_LIMITS.MACHINE_NUMBER,
  phase_number: CHANNEL_SETTINGS_LIMITS.PHASE_NUMBER,
};

type DraftValues = Record<string, Partial<Record<ConsoleField, string>>>;
type DirtyKeys = Record<string, true>;

const draftKey = (channelNumber: number, field: ConsoleField) => `${channelNumber}:${field}`;

const ConsoleSetup: React.FC<ComponentProps> = ({ user, getChannelsConsoleSetup }) => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [channels, setChannels] = useState<IChannelConsoleSetup[]>([]);
  const [drafts, setDrafts] = useState<DraftValues>({});
  const [dirtyKeys, setDirtyKeys] = useState<DirtyKeys>({});

  const allowedActionsForUser = useMemo(() => {
    if (!user?.memberData?.role) {
      return [];
    }

    return CONTROLLER_ACTIONS_BY_ROLE[user.memberData.role];
  }, [user]);

  const allowedChannelActionsForUser = useMemo(() => {
    if (!user?.memberData?.role) {
      return [];
    }

    return CHANNEL_ACTIONS_BY_ROLE[user.memberData.role];
  }, [user]);

  const fetchData = useCallback(async () => {
    const result = await getChannelsConsoleSetup(id);
    setChannels(result || []);
    setIsLoading(false);
  }, [id]);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, EXTENDED_INTERVAL_UPDATE_DATA);

    return () => clearInterval(intervalId);
  }, [fetchData]);

  // Keep local drafts in sync with the server, but never clobber a cell the
  // user is actively editing (or has an unsaved change for).
  useEffect(() => {
    setDrafts((prev) => {
      const next = { ...prev };

      channels.forEach((channel) => {
        const key = channel.channelNumber;
        next[key] = { ...next[key] };

        (Object.keys(FIELD_ACTIONS) as ConsoleField[]).forEach((field) => {
          if (!dirtyKeys[draftKey(key, field)]) {
            next[key][field] = channel[field] === null || channel[field] === undefined ? '' : String(channel[field]);
          }
        });
      });

      return next;
    });
  }, [channels, dirtyKeys]);

  const setDraftValue = (channelNumber: number, field: ConsoleField, value: string) => {
    setDrafts((prev) => ({
      ...prev,
      [channelNumber]: { ...prev[channelNumber], [field]: value },
    }));
    setDirtyKeys((prev) => ({ ...prev, [draftKey(channelNumber, field)]: true }));
  };

  const changedFields = useMemo(() => {
    const changes: { channelNumber: number; field: ConsoleField; value: string }[] = [];

    channels.forEach((channel) => {
      (Object.keys(FIELD_ACTIONS) as ConsoleField[]).forEach((field) => {
        const key = draftKey(channel.channelNumber, field);
        if (!dirtyKeys[key]) {
          return;
        }

        const draftValue = drafts[channel.channelNumber]?.[field] ?? '';
        const originalValue = channel[field] === null || channel[field] === undefined ? '' : String(channel[field]);

        if (draftValue !== originalValue) {
          changes.push({ channelNumber: channel.channelNumber, field, value: draftValue });
        }
      });
    });

    return changes;
  }, [channels, drafts, dirtyKeys]);

  const onSaveAllHandler = async () => {
    if (!changedFields.length) {
      return;
    }

    setIsSaving(true);

    const results = await Promise.all(
      changedFields.map(({ channelNumber, field, value }) =>
        changeChannelActionSilent(id, channelNumber, FIELD_ACTIONS[field], value),
      ),
    );

    setIsSaving(false);

    const succeeded = changedFields.filter((_, idx) => results[idx]);
    const failedCount = results.length - succeeded.length;

    if (succeeded.length) {
      // Don't clear the dirty flag here: a successful save only means the
      // controller accepted the command, not that it has reported the new
      // value back yet. The fetchData() below can still return the old
      // value for a moment - keeping the field dirty stops that stale
      // response from clobbering what the user just typed. changedFields
      // already stops counting a field as changed once the server value
      // catches up to the draft, so this resolves itself automatically.
      toast.success(`${succeeded.length} value${succeeded.length > 1 ? 's' : ''} updated successfully`);
    }

    if (failedCount) {
      toast.error(`${failedCount} value${failedCount > 1 ? 's' : ''} failed to update`);
    }

    await fetchData();
  };

  const canEditChannels = allowedChannelActionsForUser.includes(ChangeChannelAction.SET_CHANNEL_SHUNT_NUMBER);

  // Matches the controller firmware's own console presets against the channels'
  // current shunt/machine/phase, so this can tell you which physical console
  // (if any) the controller is actually wired to match right now.
  const currentConsoleLabel = useMemo(() => {
    if (!channels.length) {
      return null;
    }

    const matchedButton = consoleButtonActions.find((item) => {
      const pattern = CONSOLE_PATTERNS[item.actionName];
      if (!pattern) {
        return false;
      }

      return channels.every((channel) => channelMatchesConsolePattern(pattern, channel));
    });

    return matchedButton?.labelText ?? null;
  }, [channels]);

  return (
    <div className="p-edit-controller-console-setup pb-4 md:pb-25">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
          <h3 className="font-medium text-black dark:text-white">Console Setup</h3>
        </div>

        <div className="p-7">
          <h2 className="c-input-control__label flex justify-between pb-2.5 font-medium text-black dark:text-white">
            Connected console setup
          </h2>
          <div className="flex min-h-15 w-full flex-col gap-5 sm:flex-row">
            {consoleButtonActions.map((item) => {
              if (!allowedActionsForUser.includes(item.actionName)) {
                return null;
              }
              return (
                <Formik
                  key={item.labelText}
                  initialValues={{ name: '' }}
                  onSubmit={() => changeControllerPowerShare(id, item.actionName)}
                >
                  {(props) => (
                    <Form className="m-0 flex h-full w-full justify-center p-0">
                      <InteractiveButton
                        className="w-full"
                        text={item.labelText ?? 'Update'}
                        submit
                        isLoading={props.isSubmitting}
                      />
                    </Form>
                  )}
                </Formik>
              );
            })}
          </div>
          {!isLoading && (
            <p
              className={`mt-3 text-base font-bold underline ${
                currentConsoleLabel ? 'text-success' : 'text-danger'
              }`}
            >
              {currentConsoleLabel
                ? `Console currently setup as ${currentConsoleLabel}.`
                : 'Console has an unknown config.'}
            </p>
          )}
        </div>

        <div className="border-t border-stroke px-7 py-4 dark:border-strokedark">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-medium text-black dark:text-white">Per-channel shunt / machine / phase</h2>
            {canEditChannels && (
              <InteractiveButton
                type={ButtonType.Primary}
                text={isSaving ? 'Saving...' : 'Update All'}
                onClick={onSaveAllHandler}
                isLoading={isSaving}
                disabled={!changedFields.length || isSaving}
              />
            )}
          </div>

          {isLoading ? (
            <div className="flex h-full w-full items-center justify-center py-10">
              <SpinnerLoader />
            </div>
          ) : (
            <div className="max-h-[480px] overflow-y-auto rounded border border-stroke dark:border-strokedark">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-primary text-left text-xs uppercase leading-5 text-white">
                    <th className="w-1/4 py-1.5 pl-4">Channel</th>
                    <th className="w-1/4 py-1.5 pr-2">Shunt</th>
                    <th className="w-1/4 py-1.5 pr-2">Machine</th>
                    <th className="w-1/4 py-1.5 pr-4">Phase</th>
                  </tr>
                </thead>
                <tbody>
                  {channels.map((channel, idx) => (
                    <tr
                      key={channel.id}
                      className={`border-b border-stroke text-xs dark:border-strokedark ${
                        idx % 2 === 0 ? 'bg-white dark:bg-boxdark' : 'bg-gray-2 dark:bg-meta-4'
                      }`}
                    >
                      <td className="py-1 pl-4 font-medium text-black dark:text-white">
                        {channel.channel}
                        {channel.name ? <span className="ml-1 text-slate-gray">({channel.name})</span> : null}
                      </td>
                      {(Object.keys(FIELD_ACTIONS) as ConsoleField[]).map((field) => (
                        <td key={field} className="py-1 pr-2">
                          <input
                            type="number"
                            className={`${DEFAULT_INPUT_CLASS} !h-7 !py-1 !text-xs`}
                            value={drafts[channel.channelNumber]?.[field] ?? ''}
                            min={FIELD_LIMITS[field].MIN}
                            max={FIELD_LIMITS[field].MAX}
                            disabled={!canEditChannels}
                            onChange={(e) => setDraftValue(channel.channelNumber, field, e.target.value)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  user: state.user.profile,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getChannelsConsoleSetup: dispatch.channels.getChannelsConsoleSetup,
});

export default connect(mapState, mapDispatch)(ConsoleSetup);
